import React, { useState } from 'react';
import { BookOpen, Coffee, Users, Calendar, ZoomIn } from 'lucide-react';
import ImageLightbox from '../components/ImageLightbox';
import { useSiteSettings } from '../context/SiteSettingsContext';

function About() {
  const { settings, getFullAddress } = useSiteSettings();
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState({ url: '', alt: '' });

  const openLightbox = (imageUrl, imageAlt) => {
    setLightboxImage({ url: imageUrl, alt: imageAlt });
    setLightboxOpen(true);
  };

  const discipleshipOpportunities = [
    { title: 'Coffee Time', schedule: 'Every Other Thursday, 9:30 AM', icon: Coffee },
    { title: '1 Samuel Bible Study', schedule: 'Wednesday, 9:30 AM', icon: BookOpen },
    { title: 'Revelation Bible Study', schedule: 'Wednesday, 7:00 PM', icon: BookOpen },
    { title: 'Community Prayer Time', schedule: 'Saturday, 9:15 AM', icon: Users },
  ];

  return (
    <div>
      {/* Hero */}
      <div className="section-padding bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="about-title">
            About Us
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            A Place in Coos Bay for Prayer and Worship
          </p>
        </div>
      </div>

      {/* Mission & Values */}
      <div className="section-padding bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}>
            Our Foundation
          </h2>
          <div className="space-y-8 text-lg">
            <div className="card p-8">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Loving God and Others</h3>
              <p className="text-slate-700 leading-relaxed">
                {settings.about_intro || `At ${settings.church_name}, we believe in the transformative power of love. Our church is built on the foundation of loving God with all our hearts and loving our neighbors as ourselves.`}
              </p>
            </div>
            <div className="card p-8">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Biblical Teaching</h3>
              <p className="text-slate-700 leading-relaxed">
                {settings.about_biblical_teaching || 'We are committed to teaching the Word of God with clarity and conviction. Our sermons and Bible studies are grounded in Scripture, helping our congregation grow in faith and understanding.'}
              </p>
            </div>
            <div className="card p-8">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Traditional Worship</h3>
              <p className="text-slate-700 leading-relaxed">
                {settings.about_worship || 'We honor the rich traditions of Christian worship while creating a warm and welcoming atmosphere for all who enter our doors. Our services blend reverence with relevance.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Discipleship Opportunities */}
      <div className="section-padding" style={{ backgroundColor: '#fffbeb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }} data-testid="discipleship-title">
            Discipleship Opportunities
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {discipleshipOpportunities.map((opportunity, index) => {
              const Icon = opportunity.icon;
              return (
                <div key={index} className="card p-8" data-testid={`discipleship-${index}`}>
                  <Icon size={48} color="#f59e0b" className="mb-4" />
                  <h3 className="text-2xl font-bold mb-3" style={{ color: '#1e3a8a' }}>{opportunity.title}</h3>
                  <p className="text-xl text-slate-600">{opportunity.schedule}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Pastor Section */}
      <div className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-white rounded-xl p-4 shadow-md mb-6">
              <img 
                src={settings.logo_url || "https://customer-assets.emergentagent.com/job_1f958f9a-6dc9-4c4c-9c01-3c2b02fb839c/artifacts/utqs21ui_4.jpg"}
                alt={`${settings.church_name} Logo`}
                className="h-24 w-auto"
              />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}>
              Our Pastoral Leadership
            </h2>
            <div className="flex items-center justify-center mt-4 gap-3">
              <div className="h-1 w-24 bg-amber-500 rounded-full"></div>
              <div className="h-2 w-2 bg-blue-900 rounded-full"></div>
              <div className="h-1 w-24 bg-amber-500 rounded-full"></div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div 
                className="relative group cursor-pointer"
                onClick={() => openLightbox(settings.pastor_image_url || 'https://customer-assets.emergentagent.com/job_faith-community-59/artifacts/ndzngqgc_mark%20and%20wife.avif', `${settings.pastor_name} & Family`)}
                data-testid="pastor-image"
              >
                <img 
                  src={settings.pastor_image_url || "https://customer-assets.emergentagent.com/job_faith-community-59/artifacts/ndzngqgc_mark%20and%20wife.avif"}
                  alt={`${settings.pastor_name} and Family`}
                  className="rounded-xl shadow-2xl w-full transition-transform duration-300 group-hover:scale-[1.02]"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center rounded-xl">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white rounded-full p-4 shadow-lg">
                    <ZoomIn size={28} className="text-blue-900" />
                  </div>
                </div>
              </div>
              <p className="text-center text-xl font-semibold mt-4" style={{ color: '#1e3a8a' }}>{settings.pastor_name} & Family</p>
            </div>
            <div className="space-y-6">
              <div className="card p-6">
                <h3 className="text-3xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Shepherding with Heart</h3>
                <p className="text-xl text-slate-700 leading-relaxed">
                  {settings.pastor_bio || `We are blessed to have ${settings.pastor_name} and his family serving our congregation with dedication, love, and biblical wisdom. Their commitment to shepherding our church family with compassionate care and authentic leadership has been a tremendous blessing to our community.`}
                </p>
              </div>
              <div className="card p-6">
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Here for You</h3>
                <p className="text-xl text-slate-700 leading-relaxed">
                  Whether you need prayer, counsel, or simply want to connect, our pastoral team&apos;s doors and hearts are always open. We believe in building authentic relationships and walking alongside each person in their faith journey.
                </p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-900">
                <p className="text-lg text-slate-700 mb-3">
                  <strong style={{ color: '#1e3a8a' }}>Contact the Church Office:</strong>
                </p>
                <p className="text-lg text-slate-700">
                  📞 Phone: {settings.phone}<br />
                  ✉️ Email: {settings.email}<br />
                  📍 {getFullAddress()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Location */}
      <div className="section-padding" style={{ backgroundColor: '#fffbeb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}>
            Visit Us
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div 
              className="relative group cursor-pointer"
              onClick={() => openLightbox(settings.visit_us_image_url || 'https://images.unsplash.com/photo-1694878276633-dc6bcba7ee33?crop=entropy&cs=srgb&fm=jpg&q=85', 'Church building')}
              data-testid="church-building-image"
            >
              <img 
                src={settings.visit_us_image_url || "https://images.unsplash.com/photo-1694878276633-dc6bcba7ee33?crop=entropy&cs=srgb&fm=jpg&q=85"} 
                alt="Church building" 
                className="rounded-xl shadow-lg w-full transition-transform duration-300 group-hover:scale-[1.02]"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center rounded-xl">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white rounded-full p-4 shadow-lg">
                  <ZoomIn size={28} className="text-blue-900" />
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#1e3a8a' }}>Address</h3>
                <p className="text-xl text-slate-700">{settings.address}</p>
                <p className="text-xl text-slate-700">{settings.city_state_zip}</p>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#1e3a8a' }}>Phone</h3>
                <p className="text-xl text-slate-700">{settings.phone}</p>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#1e3a8a' }}>Service Times</h3>
                <p className="text-xl text-slate-700">Sunday School: {settings.sunday_school_time}</p>
                <p className="text-xl text-slate-700">Sunday Service: {settings.sunday_service_time}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        imageUrl={lightboxImage.url}
        imageAlt={lightboxImage.alt}
      />
    </div>
  );
}

export default About;